import type { ResponsiveLayouts } from 'react-grid-layout';

export type DashboardWorkspaceScope = 'terminal.home' | 'trading-dna.report' | 'trading-dna.analytics';
export type DashboardLayoutSource = 'default' | 'local' | 'account';
export type TradingDnaLayoutPreset = 'balanced' | 'pnl-focus' | 'points-focus';

export interface DashboardLayoutPreference {
  id: string;
  scope: DashboardWorkspaceScope;
  ownerRef: 'current-user';
  version: number;
  source: DashboardLayoutSource;
  updatedAt: string;
  preset?: TradingDnaLayoutPreset;
  responsiveLayouts?: ResponsiveLayouts;
  hiddenPanels?: string[];
  pinnedPanels?: string[];
}

export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

interface DashboardLayoutPreferenceOptions {
  allowedPanelIds?: readonly string[];
  allowedPresets?: readonly TradingDnaLayoutPreset[];
}

interface DashboardLayoutPreferenceBuildOptions extends DashboardLayoutPreferenceOptions {
  updatedAt?: string;
}

interface SaveDashboardLayoutPreferenceInput extends DashboardLayoutPreferenceOptions {
  scope: DashboardWorkspaceScope;
  preset?: TradingDnaLayoutPreset;
  responsiveLayouts?: ResponsiveLayouts;
  hiddenPanels?: readonly string[];
  pinnedPanels?: readonly string[];
}

interface AccountDashboardLayoutPreferenceInput extends DashboardLayoutPreferenceBuildOptions {
  scope: DashboardWorkspaceScope;
  preset?: TradingDnaLayoutPreset;
  responsiveLayouts?: ResponsiveLayouts;
  hiddenPanels?: readonly string[];
  pinnedPanels?: readonly string[];
  clientRevision?: string;
}

export interface DashboardLayoutItemPreferenceContract {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  min_w?: number;
  max_w?: number;
  min_h?: number;
  max_h?: number;
  static?: boolean;
  is_draggable?: boolean;
  is_resizable?: boolean;
}

export interface DashboardLayoutPreferenceContract {
  id: string;
  scope: DashboardWorkspaceScope;
  owner_ref: 'current-user';
  version: number;
  source: 'account';
  updated_at: string;
  preset?: TradingDnaLayoutPreset;
  responsive_layouts?: Record<string, DashboardLayoutItemPreferenceContract[]>;
  hidden_panels?: string[];
  pinned_panels?: string[];
}

export interface AccountDashboardLayoutPreferenceUpsert {
  preference: DashboardLayoutPreferenceContract;
  client_revision: string;
}

export const DASHBOARD_LAYOUT_PREFERENCE_VERSION = 1;
export const DASHBOARD_LAYOUT_PREFERENCE_STORAGE_PREFIX = 'tm-terminal.dashboard-layout-preferences.v1';
export const DASHBOARD_LAYOUT_PREFERENCE_ACCOUNT_API_BASE = '/api/dashboard-layout-preferences';
export const TRADING_DNA_REPORT_LAYOUT_PRESETS: { key: TradingDnaLayoutPreset; labelKey: string }[] = [
  { key: 'balanced', labelKey: 'trading_dna.report.layout.balanced' },
  { key: 'pnl-focus', labelKey: 'trading_dna.report.layout.pnl_focus' },
  { key: 'points-focus', labelKey: 'trading_dna.report.layout.points_focus' },
];

const BREAKPOINTS = ['lg', 'md', 'sm', 'xs', 'xxs'] as const;
const BREAKPOINT_SET = new Set<string>(BREAKPOINTS);
const LOCAL_LAYOUT_SOURCE: DashboardLayoutSource = 'local';
const ACCOUNT_LAYOUT_SOURCE: DashboardLayoutSource = 'account';
const INT32_MIN = -2_147_483_648;
const INT32_MAX = 2_147_483_647;

type RecordValue = Record<string, unknown>;

export function dashboardLayoutPreferenceStorageKey(scope: DashboardWorkspaceScope): string {
  return `${DASHBOARD_LAYOUT_PREFERENCE_STORAGE_PREFIX}.${scope}`;
}

export function dashboardLayoutPreferenceAccountPath(scope: DashboardWorkspaceScope): string {
  return `${DASHBOARD_LAYOUT_PREFERENCE_ACCOUNT_API_BASE}/${encodeURIComponent(scope)}`;
}

export function loadDashboardLayoutPreference(
  scope: DashboardWorkspaceScope,
  options: DashboardLayoutPreferenceOptions = {},
): DashboardLayoutPreference | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(dashboardLayoutPreferenceStorageKey(scope));
    if (!raw) return null;
    return parseDashboardLayoutPreference(raw, scope, options);
  } catch {
    return null;
  }
}

export function saveDashboardLayoutPreference(input: SaveDashboardLayoutPreferenceInput): void {
  if (typeof window === 'undefined') return;

  const preference = sanitizeDashboardLayoutPreference(
    {
      id: `${input.scope}:current-user`,
      scope: input.scope,
      ownerRef: 'current-user',
      version: DASHBOARD_LAYOUT_PREFERENCE_VERSION,
      source: LOCAL_LAYOUT_SOURCE,
      updatedAt: new Date().toISOString(),
      preset: input.preset,
      responsiveLayouts: input.responsiveLayouts,
      hiddenPanels: input.hiddenPanels,
      pinnedPanels: input.pinnedPanels,
    },
    input.scope,
    input,
  );

  if (!preference) return;

  try {
    window.localStorage.setItem(dashboardLayoutPreferenceStorageKey(input.scope), JSON.stringify(preference));
  } catch {
    // Local dashboard persistence is a convenience, not a report dependency.
  }
}

export function clearDashboardLayoutPreference(scope: DashboardWorkspaceScope): void {
  try {
    window.localStorage.removeItem(dashboardLayoutPreferenceStorageKey(scope));
  } catch {
    // localStorage unavailable
  }
}

export function parseDashboardLayoutPreference(
  raw: string,
  expectedScope: DashboardWorkspaceScope,
  options: DashboardLayoutPreferenceOptions = {},
): DashboardLayoutPreference | null {
  try {
    return sanitizeDashboardLayoutPreference(JSON.parse(raw), expectedScope, options);
  } catch {
    return null;
  }
}

export function buildAccountDashboardLayoutPreferenceUpsert(
  input: AccountDashboardLayoutPreferenceInput,
): AccountDashboardLayoutPreferenceUpsert | null {
  const preference = sanitizeDashboardLayoutPreference(
    {
      id: `${input.scope}:current-user`,
      scope: input.scope,
      ownerRef: 'current-user',
      version: DASHBOARD_LAYOUT_PREFERENCE_VERSION,
      source: ACCOUNT_LAYOUT_SOURCE,
      updatedAt: input.updatedAt ?? new Date().toISOString(),
      preset: input.preset,
      responsiveLayouts: input.responsiveLayouts,
      hiddenPanels: input.hiddenPanels,
      pinnedPanels: input.pinnedPanels,
    },
    input.scope,
    input,
    ACCOUNT_LAYOUT_SOURCE,
  );

  if (!preference) return null;

  return {
    preference: toAccountPreferenceContract(preference),
    client_revision: input.clientRevision ?? `dashboard-layout-preferences.v${DASHBOARD_LAYOUT_PREFERENCE_VERSION}`,
  };
}

export function loadTradingDnaReportLayoutPreset(): TradingDnaLayoutPreset {
  const preference = loadDashboardLayoutPreference('trading-dna.report', {
    allowedPresets: TRADING_DNA_REPORT_LAYOUT_PRESETS.map((preset) => preset.key),
  });
  return preference?.preset ?? loadLegacyTradingDnaReportLayoutPreset() ?? 'balanced';
}

export function saveTradingDnaReportLayoutPreset(preset: TradingDnaLayoutPreset): void {
  saveDashboardLayoutPreference({
    scope: 'trading-dna.report',
    preset,
    allowedPresets: TRADING_DNA_REPORT_LAYOUT_PRESETS.map((option) => option.key),
  });
  clearLegacyTradingDnaReportLayoutPreset();
}

function sanitizeDashboardLayoutPreference(
  value: unknown,
  expectedScope: DashboardWorkspaceScope,
  options: DashboardLayoutPreferenceOptions,
  expectedSource: DashboardLayoutSource = LOCAL_LAYOUT_SOURCE,
): DashboardLayoutPreference | null {
  if (!isRecord(value)) return null;
  if (value.version !== DASHBOARD_LAYOUT_PREFERENCE_VERSION) return null;
  if (value.scope !== expectedScope) return null;
  if (value.ownerRef !== 'current-user') return null;
  if (value.source !== expectedSource) return null;
  if (typeof value.id !== 'string' || value.id.length > 120) return null;
  if (typeof value.updatedAt !== 'string' || value.updatedAt.length > 80) return null;

  const preference: DashboardLayoutPreference = {
    id: value.id,
    scope: expectedScope,
    ownerRef: 'current-user',
    version: DASHBOARD_LAYOUT_PREFERENCE_VERSION,
    source: expectedSource,
    updatedAt: value.updatedAt,
  };

  const preset = sanitizePreset(value.preset, options.allowedPresets);
  if (preset) preference.preset = preset;

  const responsiveLayouts = sanitizeResponsiveLayouts(value.responsiveLayouts, options.allowedPanelIds);
  if (responsiveLayouts) preference.responsiveLayouts = responsiveLayouts;

  const hiddenPanels = sanitizePanelIdList(value.hiddenPanels, options.allowedPanelIds);
  if (hiddenPanels) preference.hiddenPanels = hiddenPanels;

  const pinnedPanels = sanitizePanelIdList(value.pinnedPanels, options.allowedPanelIds);
  if (pinnedPanels) preference.pinnedPanels = pinnedPanels;

  return preference;
}

function sanitizePreset(
  value: unknown,
  allowedPresets: readonly TradingDnaLayoutPreset[] | undefined,
): TradingDnaLayoutPreset | undefined {
  if (value !== 'balanced' && value !== 'pnl-focus' && value !== 'points-focus') return undefined;
  if (allowedPresets && !allowedPresets.includes(value)) return undefined;
  return value;
}

function sanitizeResponsiveLayouts(
  value: unknown,
  allowedPanelIds: readonly string[] | undefined,
): ResponsiveLayouts | undefined {
  if (!isRecord(value) || !allowedPanelIds || allowedPanelIds.length === 0) return undefined;

  const allowedIds = new Set(allowedPanelIds);
  const layouts: Record<string, DashboardLayoutItem[]> = {};
  for (const [breakpoint, items] of Object.entries(value)) {
    if (!BREAKPOINT_SET.has(breakpoint) || !Array.isArray(items)) continue;
    const sanitizedItems = items
      .map((item) => sanitizeLayoutItem(item, allowedIds))
      .filter((item): item is DashboardLayoutItem => item !== null);
    if (sanitizedItems.length > 0) layouts[breakpoint] = sanitizedItems;
  }

  return Object.keys(layouts).length > 0 ? (layouts as ResponsiveLayouts) : undefined;
}

function sanitizeLayoutItem(value: unknown, allowedIds: Set<string>): DashboardLayoutItem | null {
  if (!isRecord(value)) return null;
  if (typeof value.i !== 'string' || !allowedIds.has(value.i)) return null;
  if (
    !isWireInteger(value.x) ||
    !isWireInteger(value.y) ||
    !isWireInteger(value.w) ||
    !isWireInteger(value.h)
  ) {
    return null;
  }

  const layout: DashboardLayoutItem = {
    i: value.i,
    x: value.x,
    y: value.y,
    w: value.w,
    h: value.h,
  };

  for (const key of ['minW', 'maxW', 'minH', 'maxH'] as const) {
    if (isWireInteger(value[key])) layout[key] = value[key];
  }

  for (const key of ['static', 'isDraggable', 'isResizable'] as const) {
    if (typeof value[key] === 'boolean') layout[key] = value[key];
  }

  return layout;
}

function sanitizePanelIdList(value: unknown, allowedPanelIds: readonly string[] | undefined): string[] | undefined {
  if (!Array.isArray(value) || !allowedPanelIds || allowedPanelIds.length === 0) return undefined;
  const allowedIds = new Set(allowedPanelIds);
  const sanitized = Array.from(
    new Set(value.filter((panelId): panelId is string => typeof panelId === 'string' && allowedIds.has(panelId))),
  );
  return sanitized.length > 0 ? sanitized : undefined;
}

function isWireInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= INT32_MIN && value <= INT32_MAX;
}

function isRecord(value: unknown): value is RecordValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toAccountPreferenceContract(preference: DashboardLayoutPreference): DashboardLayoutPreferenceContract {
  const contract: DashboardLayoutPreferenceContract = {
    id: preference.id,
    scope: preference.scope,
    owner_ref: preference.ownerRef,
    version: preference.version,
    source: 'account',
    updated_at: preference.updatedAt,
  };

  if (preference.preset) contract.preset = preference.preset;
  if (preference.hiddenPanels?.length) contract.hidden_panels = preference.hiddenPanels;
  if (preference.pinnedPanels?.length) contract.pinned_panels = preference.pinnedPanels;
  if (preference.responsiveLayouts) {
    const responsiveLayouts = Object.fromEntries(
      Object.entries(preference.responsiveLayouts)
        .filter((entry): entry is [string, DashboardLayoutItem[]] => Array.isArray(entry[1]))
        .map(([breakpoint, items]) => [breakpoint, items.map(toAccountLayoutItemContract)]),
    );
    if (Object.keys(responsiveLayouts).length > 0) contract.responsive_layouts = responsiveLayouts;
  }

  return contract;
}

function toAccountLayoutItemContract(item: DashboardLayoutItem): DashboardLayoutItemPreferenceContract {
  const contract: DashboardLayoutItemPreferenceContract = {
    i: item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  };

  if (item.minW !== undefined) contract.min_w = item.minW;
  if (item.maxW !== undefined) contract.max_w = item.maxW;
  if (item.minH !== undefined) contract.min_h = item.minH;
  if (item.maxH !== undefined) contract.max_h = item.maxH;
  if (item.static !== undefined) contract.static = item.static;
  if (item.isDraggable !== undefined) contract.is_draggable = item.isDraggable;
  if (item.isResizable !== undefined) contract.is_resizable = item.isResizable;

  return contract;
}

const LEGACY_TRADING_DNA_REPORT_LAYOUT_KEY = 'tm-terminal.trading-dna.dashboard-layout.v1';

function loadLegacyTradingDnaReportLayoutPreset(): TradingDnaLayoutPreset | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(LEGACY_TRADING_DNA_REPORT_LAYOUT_KEY);
    return value === 'balanced' || value === 'pnl-focus' || value === 'points-focus' ? value : null;
  } catch {
    return null;
  }
}

function clearLegacyTradingDnaReportLayoutPreset(): void {
  try {
    window.localStorage.removeItem(LEGACY_TRADING_DNA_REPORT_LAYOUT_KEY);
  } catch {
    // localStorage unavailable
  }
}
